import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Center,
    Button,
    Grid,
    Badge,
    HStack,
    Input,
} from "@chakra-ui/react";
import { usePayment } from "../hooks/usePayment";

interface Package {
    id: string;
    name: string;
    years: number;
    price: number;
    savings?: number;
    popular?: boolean;
}

const Upgrade: React.FC = () => {
    const { paymentState, processPayment, setSelectedPackage, setUserEmail, redirectToCheckout } = usePayment();
    const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [emailInput, setEmailInput] = useState("");
    const [showEmailInput, setShowEmailInput] = useState(false);

    const packages: Package[] = [
        {
            id: "1year",
            name: "1 Year Plan",
            years: 1,
            price: 20,
        },
        {
            id: "2year",
            name: "2 Year Plan",
            years: 2,
            price: 35,
            savings: 12.5,
            popular: true,
        },
        {
            id: "4year",
            name: "4 Year Plan",
            years: 4,
            price: 60,
            savings: 16.0,
        },
    ];

    const premiumFeatures = [
        "Unlimited Final GPA Projections",
        "Free AI Grade Predictions",
        "Unlimited Courses ",
        "Unlimited Transcript Processing",
        "Unlimited access to Past Questions",
        "Unlimited Transcript Downloads",
    ];

    const handlePackageSelect = async (pkg: Package) => {
        setSelectedPackageId(pkg.id);
        await setSelectedPackage(pkg.id);
    };

    const handlePayment = async () => {
        if (!selectedPackageId) {
            return;
        }

        if (paymentState.paymentStatus === "url_ready") {
            redirectToCheckout();
            return;
        }

        // Determine which email to use - prioritize emailInput if it exists and is different
        const emailToUse = emailInput.trim() || paymentState.userEmail;

        // Check if email is missing
        if (!emailToUse) {
            // Show email input field
            setShowEmailInput(true);
            return;
        }

        // If emailInput has a value (user typed a new email), update state and use it
        if (emailInput.trim() && emailInput.trim() !== paymentState.userEmail) {
            setUserEmail(emailInput.trim());
        }

        setIsProcessing(true);
        try {
            // Always pass the email to use (from input or state) to ensure correct email is used
            const result = await processPayment(emailToUse);
            // Don't set isProcessing to false here - let it persist until payment status changes
            // The loading will be controlled by paymentState.paymentStatus
            if (result.success === "pending") {
                // Payment dialog opened, keep loading state
                return;
            }
        } catch (error) {
            console.error(error);
            setIsProcessing(false);
            alert(
                "An error occurred while processing your payment. Please try again or contact support."
            );
        }
    };

    // Update loading state based on payment status
    useEffect(() => {
        if (
            paymentState.paymentStatus === "success" ||
            (paymentState.errorStatus && paymentState.errorMessage)
        ) {
            setIsProcessing(false);

            // If error is related to email (not registered or invalid), show email input again
            if (
                paymentState.errorStatus &&
                paymentState.errorMessage &&
                (paymentState.errorMessage.includes("email") ||
                    paymentState.errorMessage.includes("not registered") ||
                    paymentState.errorMessage.includes("not valid"))
            ) {
                setShowEmailInput(true);
            }
        }
    }, [paymentState.paymentStatus, paymentState.errorStatus, paymentState.errorMessage]);

    // Pre-fill email input when showing after error (separate effect to avoid dependency issues)
    useEffect(() => {
        if (
            showEmailInput &&
            !emailInput &&
            paymentState.userEmail &&
            paymentState.errorStatus &&
            paymentState.errorMessage &&
            (paymentState.errorMessage.includes("email") ||
                paymentState.errorMessage.includes("not registered") ||
                paymentState.errorMessage.includes("not valid"))
        ) {
            setEmailInput(paymentState.userEmail);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showEmailInput,
        paymentState.userEmail,
        paymentState.errorStatus,
        paymentState.errorMessage,
    ]);

    return (
        <Box
            minH="100vh"
            bg="gray.50"
            backgroundImage="url('/assets/images/bck.png')"
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
            py={10}
        >
            <Container maxW="container.xl" py={10}>
                <VStack gap={8} align="stretch">
                    {/* Header */}
                    <Center>
                        <VStack gap={4}>
                            <Heading size="2xl" fontWeight="bold" color="teal.700">
                                GradeR - Premium Plans
                            </Heading>
                            <Text textAlign="center" color="gray.600" fontSize="lg" maxW="600px">
                                Unlock all premium features and get unlimited access to everything
                                GradeR has to offer
                            </Text>
                        </VStack>
                    </Center>

                    {/* Premium Features */}
                    <Box bg="white" p={6} borderRadius="lg" boxShadow="md" maxW="800px" mx="auto">
                        <Heading size="md" mb={4} color="teal.700">
                            Premium Features
                        </Heading>
                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={3}>
                            {premiumFeatures.map((feature, index) => (
                                <HStack key={index} align="start" gap={2}>
                                    <Text
                                        color="teal.500"
                                        fontSize="lg"
                                        fontWeight="bold"
                                        mt={0.5}
                                        flexShrink={0}
                                    >
                                        ✓
                                    </Text>
                                    <Text color="gray.700">{feature}</Text>
                                </HStack>
                            ))}
                        </Grid>
                    </Box>

                    {/* Package Cards */}
                    <Grid
                        templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                        gap={6}
                        maxW="1200px"
                        mx="auto"
                    >
                        {packages.map((pkg) => {
                            const isSelected = selectedPackageId === pkg.id;
                            const regularPrice = pkg.years * 20; // 1 year = GHS 20

                            return (
                                <Box
                                    key={pkg.id}
                                    bg="white"
                                    p={6}
                                    borderRadius="lg"
                                    boxShadow={isSelected ? "xl" : "md"}
                                    borderWidth={isSelected ? "2px" : "1px"}
                                    borderColor={isSelected ? "teal.500" : "gray.200"}
                                    position="relative"
                                    transition="all 0.3s"
                                    _hover={{
                                        boxShadow: "xl",
                                        transform: "translateY(-4px)",
                                    }}
                                    cursor="pointer"
                                    onClick={() => handlePackageSelect(pkg)}
                                >
                                    {pkg.popular && (
                                        <Badge
                                            colorScheme="teal"
                                            position="absolute"
                                            top={-3}
                                            right={4}
                                            px={3}
                                            py={1}
                                            borderRadius="full"
                                        >
                                            Most Popular
                                        </Badge>
                                    )}

                                    <VStack align="stretch" gap={4}>
                                        <VStack align="start" gap={2}>
                                            <Heading size="lg" color="teal.700">
                                                {pkg.name}
                                            </Heading>
                                            {pkg.savings && (
                                                <Badge colorScheme="green" fontSize="sm">
                                                    Save {pkg.savings}%
                                                </Badge>
                                            )}
                                        </VStack>

                                        <Box>
                                            <HStack align="baseline" gap={2}>
                                                <Text
                                                    fontSize="3xl"
                                                    fontWeight="bold"
                                                    color="teal.700"
                                                >
                                                    GHS {pkg.price}
                                                </Text>
                                                {pkg.savings && (
                                                    <Text
                                                        fontSize="lg"
                                                        color="gray.500"
                                                        textDecoration="line-through"
                                                    >
                                                        GHS {regularPrice}
                                                    </Text>
                                                )}
                                            </HStack>
                                            <Text fontSize="sm" color="gray.600">
                                                {pkg.years === 1
                                                    ? "Per year"
                                                    : `For ${pkg.years} years`}
                                            </Text>
                                        </Box>

                                        <Box
                                            bg={isSelected ? "teal.50" : "gray.50"}
                                            p={3}
                                            borderRadius="md"
                                        >
                                            <Text
                                                fontSize="sm"
                                                color="gray.700"
                                                fontWeight="medium"
                                            >
                                                {isSelected ? "✓ Selected" : "Click to select"}
                                            </Text>
                                        </Box>
                                    </VStack>
                                </Box>
                            );
                        })}
                    </Grid>

                    {/* Account Info */}
                    {(emailInput.trim() || paymentState.userEmail) && (
                        <Box
                            bg="white"
                            p={6}
                            borderRadius="lg"
                            boxShadow="md"
                            maxW="600px"
                            mx="auto"
                        >
                            <VStack align="stretch" gap={3}>
                                <Text fontWeight="bold" fontSize="lg" color="gray.800">
                                    Account Details
                                </Text>
                                <Text>
                                    Account Email:{" "}
                                    <Text as="span" fontStyle="italic" color="gray.700">
                                        {emailInput.trim() || paymentState.userEmail}
                                    </Text>
                                </Text>
                                {selectedPackageId && (
                                    <Text>
                                        Selected Plan:{" "}
                                        <Text as="span" fontWeight="bold" color="teal.700">
                                            {packages.find((p) => p.id === selectedPackageId)?.name}{" "}
                                            - GHS{" "}
                                            {
                                                packages.find((p) => p.id === selectedPackageId)
                                                    ?.price
                                            }
                                        </Text>
                                    </Text>
                                )}
                            </VStack>
                        </Box>
                    )}

                    {/* Error Message */}
                    {paymentState.errorStatus && paymentState.errorMessage && (
                        <Box
                            bg="red.50"
                            borderWidth="1px"
                            borderColor="red.200"
                            p={4}
                            borderRadius="md"
                            maxW="600px"
                            mx="auto"
                        >
                            <Text color="red.600" textAlign="center">
                                {paymentState.errorMessage}
                            </Text>
                        </Box>
                    )}

                    {/* Email Input Field */}
                    {(showEmailInput ||
                        (paymentState.errorStatus &&
                            paymentState.errorMessage &&
                            (paymentState.errorMessage.includes("email") ||
                                paymentState.errorMessage.includes("not registered") ||
                                paymentState.errorMessage.includes("not valid")))) && (
                            <Box
                                bg="white"
                                p={6}
                                borderRadius="lg"
                                boxShadow="md"
                                maxW="600px"
                                mx="auto"
                            >
                                <VStack align="stretch" gap={4}>
                                    <Text color="gray.700" fontWeight="medium" fontSize="md">
                                        GradeR Account Email
                                    </Text>
                                    <Input
                                        type="email"
                                        placeholder="Enter your GradeR account email"
                                        value={emailInput}
                                        onChange={(e) => {
                                            setEmailInput(e.target.value);
                                        }}
                                        size="lg"
                                        borderColor={
                                            paymentState.errorStatus && paymentState.errorMessage
                                                ? "red.300"
                                                : "gray.300"
                                        }
                                        _focus={{
                                            borderColor:
                                                paymentState.errorStatus && paymentState.errorMessage
                                                    ? "red.500"
                                                    : "teal.500",
                                            boxShadow:
                                                paymentState.errorStatus && paymentState.errorMessage
                                                    ? "0 0 0 1px red.500"
                                                    : "0 0 0 1px teal.500",
                                        }}
                                    />
                                    <Text fontSize="sm" color="gray.600">
                                        Please enter the email address associated with your GradeR
                                        account
                                    </Text>
                                </VStack>
                            </Box>
                        )}

                    {/* Payment Button */}
                    {paymentState.paymentStatus !== "success" &&
                        selectedPackageId && (
                            <Center>
                                <Button
                                    colorScheme="teal"
                                    size="lg"
                                    onClick={handlePayment}
                                    loading={
                                        paymentState.paymentStatus === "processing"
                                    }
                                    loadingText="Processing..."
                                    disabled={
                                        !selectedPackageId ||
                                        paymentState.paymentStatus === "processing" ||
                                        paymentState.paymentStatus === "awaiting_payment"
                                    }
                                    px={8}
                                    py={6}
                                    fontSize="lg"
                                >
                                    {paymentState.paymentStatus === "url_ready"
                                        ? "Checkout"
                                        : paymentState.paymentStatus === "awaiting_payment"
                                            ? "Awaiting Payment"
                                            : "Proceed to Payment"}
                                </Button>
                            </Center>
                        )}

                    {/* Success Message */}
                    {paymentState.paymentStatus === "success" && (
                        <Box
                            bg="green.50"
                            borderWidth="1px"
                            borderColor="green.200"
                            p={6}
                            borderRadius="md"
                            maxW="600px"
                            mx="auto"
                        >
                            <VStack gap={3}>
                                <Text
                                    color="green.600"
                                    textAlign="center"
                                    fontSize="lg"
                                    fontWeight="medium"
                                >
                                    ✓ Payment Successful!
                                </Text>
                                <Text color="green.700" textAlign="center">
                                    {paymentState.isExtension
                                        ? "Thank you for extending your subscription! Your access has been updated."
                                        : "Thank you for your payment. You now have access to all our premium features."}
                                </Text>
                                {paymentState.expiryDate && (
                                    <Text color="green.700" textAlign="center" fontWeight="bold">
                                        Expires on: {new Date(paymentState.expiryDate).toLocaleDateString("en-GB", {
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric",
                                        })}
                                    </Text>
                                )}
                            </VStack>
                        </Box>
                    )}

                    {/* App Version */}
                    <Text textAlign="center" color="gray.500" fontSize="xs" mt={4}>
                        App Version: 1.2.8
                    </Text>
                </VStack>
            </Container>
        </Box>
    );
};

export default Upgrade;
